import React from 'react'
import {
  Modal,
  Button,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  ProgressBar,
} from 'react-bootstrap'
import { Ansible } from 'interfaces/ansible'
import useAvailableAnsibles from 'hooks/use-available-ansibles'
import { useDeviceAssignmentFlow } from 'hooks/use-device-assignment-flow'

interface Device {
  id: string
  name: string
  type: 'mac-mini' | 'physical-phone'
}

interface DeviceAssignmentModalProps {
  show: boolean
  onHide: () => void
  devices: Device[]
  terminalId: string
  terminalName?: string
  onSuccess?: () => void
}

const DeviceAssignmentModal: React.FC<DeviceAssignmentModalProps> = ({
  show,
  onHide,
  devices,
  terminalId,
  terminalName,
  onSuccess,
}) => {
  const macMiniCount = devices.filter((d) => d.type === 'mac-mini').length
  const phoneCount = devices.filter((d) => d.type === 'physical-phone').length

  const {
    availableAnsibles,
    isLoading: ansiblesLoading,
    error: ansiblesError,
  } = useAvailableAnsibles({
    terminalId,
    requiredMacMinis: macMiniCount,
    requiredPhones: phoneCount,
    enabled: show && devices.length > 0,
  })

  const { assignDevicesToAnsible, isAssigning, assignmentProgress } =
    useDeviceAssignmentFlow()

  const handleAnsibleSelect = async (ansible: Ansible) => {
    try {
      await assignDevicesToAnsible(devices, ansible, terminalId)
      onSuccess?.()
      onHide()
    } catch (error) {
      // Error is already handled in the hook
    }
  }

  const getCapacityInfo = (ansible: Ansible) => {
    const availableMacMinis = ansible.maxMacMinis - ansible.currentMacMinis
    const availablePhones =
      ansible.maxPhysicalPhones - ansible.currentPhysicalPhones

    return {
      availableMacMinis,
      availablePhones,
      totalAvailable: availableMacMinis + availablePhones,
    }
  }

  const getCapacityVariant = (used: number, max: number) => {
    const percentage = (used / max) * 100
    if (percentage < 70) return 'success'
    if (percentage < 90) return 'warning'
    return 'danger'
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Assign {devices.length} Device{devices.length === 1 ? '' : 's'} to
          Ansible
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Assignment Progress */}
        {isAssigning && assignmentProgress && (
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <div className="flex-grow-1">
                <div>{assignmentProgress.currentAction}</div>
                <ProgressBar
                  now={
                    (assignmentProgress.current / assignmentProgress.total) *
                    100
                  }
                  className="mt-2"
                  variant="info"
                />
              </div>
            </div>
          </Alert>
        )}

        {/* Device Summary */}
        <Card className="mb-3">
          <Card.Body>
            <h6>Assignment Summary</h6>
            <p className="mb-2">
              <strong>Terminal:</strong> {terminalName || 'Unknown'}
            </p>
            <p className="mb-0">
              <strong>Devices to assign:</strong>{' '}
              {macMiniCount > 0 && (
                <span className="badge bg-primary me-2">
                  {macMiniCount} Mac Mini{macMiniCount === 1 ? '' : 's'}
                </span>
              )}
              {phoneCount > 0 && (
                <span className="badge bg-info">
                  {phoneCount} Phone{phoneCount === 1 ? '' : 's'}
                </span>
              )}
            </p>
          </Card.Body>
        </Card>

        {/* Available Ansibles */}
        <h6>Select an Available Ansible:</h6>

        {ansiblesLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="mt-2">Loading available ansibles...</div>
          </div>
        ) : ansiblesError ? (
          <Alert variant="danger">
            Error loading ansibles: {ansiblesError.message}
          </Alert>
        ) : availableAnsibles.length === 0 ? (
          <Alert variant="warning">
            <h6>No Available Ansibles</h6>
            <p className="mb-0">
              There are no ansibles in terminal <strong>{terminalName}</strong>{' '}
              with sufficient capacity to handle {macMiniCount} Mac Mini
              {macMiniCount === 1 ? '' : 's'} and {phoneCount} phone
              {phoneCount === 1 ? '' : 's'}.
            </p>
          </Alert>
        ) : (
          <Row>
            {availableAnsibles.map((ansible) => {
              const capacity = getCapacityInfo(ansible)

              return (
                <Col md={6} key={ansible.id} className="mb-3">
                  <Card
                    className={`h-100 ${isAssigning ? '' : 'cursor-pointer'}`}
                    style={{
                      cursor: isAssigning ? 'not-allowed' : 'pointer',
                      opacity: isAssigning ? 0.6 : 1,
                    }}
                    onClick={() => !isAssigning && handleAnsibleSelect(ansible)}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0">{ansible.name}</h6>
                        <span
                          className={`badge bg-${
                            ansible.status === 'ACTIVE' ? 'success' : 'warning'
                          }`}
                        >
                          {ansible.status}
                        </span>
                      </div>

                      {/* Mac Mini Capacity */}
                      <div className="mb-2">
                        <small className="text-muted">Mac Minis:</small>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small">
                            {ansible.currentMacMinis}/{ansible.maxMacMinis} used
                          </span>
                          <span className="small text-success">
                            {capacity.availableMacMinis} available
                          </span>
                        </div>
                        <ProgressBar
                          now={
                            (ansible.currentMacMinis / ansible.maxMacMinis) *
                            100
                          }
                          variant={getCapacityVariant(
                            ansible.currentMacMinis,
                            ansible.maxMacMinis
                          )}
                          style={{ height: '6px' }}
                        />
                      </div>

                      {/* Phone Capacity */}
                      <div>
                        <small className="text-muted">Physical Phones:</small>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="small">
                            {ansible.currentPhysicalPhones}/
                            {ansible.maxPhysicalPhones} used
                          </span>
                          <span className="small text-success">
                            {capacity.availablePhones} available
                          </span>
                        </div>
                        <ProgressBar
                          now={
                            (ansible.currentPhysicalPhones /
                              ansible.maxPhysicalPhones) *
                            100
                          }
                          variant={getCapacityVariant(
                            ansible.currentPhysicalPhones,
                            ansible.maxPhysicalPhones
                          )}
                          style={{ height: '6px' }}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )
            })}
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isAssigning}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default DeviceAssignmentModal
